import type { BrandThreadItem, BrandThreadRevision, BrandThreadTimeline } from '../api/payment/brand-thread.api';
import type { BrandTaskStatus } from '../api/payment/brand-deal.api';
import { taskEventLabel, taskStatusLabel } from './brand-creative';

// Phòng trao đổi (spec 2026-10-12 §3.1) — hằng chữ và hàm thuần, tách khỏi component để test không cần dựng React.

export const THREAD_TEXT_MAX_LENGTH = 2000;
export const THREAD_POLL_MS = 10_000;
export const THREAD_UNREAD_POLL_MS = 60_000;
export const THREAD_READ_EVENT = 'brand-thread-read';

export const THREAD_CONTACT_WARNING =
  'Trao đổi và thanh toán ngoài Cab thì sàn không giữ tiền và không xử được tranh chấp cho bạn.';
export const THREAD_CLOSED_NOTICE = 'Phòng trao đổi đã đóng.';
export const THREAD_FAIRNESS_NOTE = 'Nhắn tin xin chỉnh không tính lượt sửa. Muốn tính lượt, dùng nút Yêu cầu sửa.';
/** Lời mời đăng bài: lượt chỉnh tính bằng nút Đề nghị chỉnh của creator, không phải Yêu cầu sửa. */
export const THREAD_FAIRNESS_NOTE_DIRECT = 'Nhắn tin xin chỉnh không tính lượt. Muốn tính lượt, creator dùng nút Đề nghị chỉnh.';
export const threadFairnessNote = (hasDirectEdit: boolean): string =>
  hasDirectEdit ? THREAD_FAIRNESS_NOTE_DIRECT : THREAD_FAIRNESS_NOTE;

export const validateThreadText = (text: string): string | null => {
  const t = (text ?? '').trim();
  return t.length < 1 || t.length > THREAD_TEXT_MAX_LENGTH ? 'Tin nhắn từ 1 đến 2.000 ký tự.' : null;
};

/** Gộp hai danh sách, bỏ trùng theo khoá (bản sau thắng), cũ trước mới sau, cùng thời điểm thì theo khoá. */
export const mergeTimeline = (current: BrandThreadItem[], incoming: BrandThreadItem[]): BrandThreadItem[] => {
  const byKey = new Map<string, BrandThreadItem>();
  [...current, ...incoming].forEach((i) => byKey.set(i.key, i));
  return Array.from(byKey.values()).sort((a, b) => {
    const diff = Date.parse(a.at) - Date.parse(b.at);
    if (diff !== 0) return diff;
    if (a.key === b.key) return 0;
    return a.key < b.key ? -1 : 1;
  });
};

/** Có tin nhắn mới của bên kia (để đánh dấu đã đọc). */
export const hasNewFromOthers = (prev: BrandThreadItem[], next: BrandThreadItem[]): boolean => {
  const known = new Set(prev.map((i) => i.key));
  return next.some((i) => !known.has(i.key) && i.type === 'message' && !i.mine);
};

const SYNTHETIC_LABELS: Record<string, string> = {
  ApplicationSent: 'Creator ứng tuyển',
  ApplicationApproved: 'Người đặt duyệt ứng tuyển',
  ApplicationRejected: 'Người đặt từ chối ứng tuyển',
  ApplicationWithdrawn: 'Creator rút đơn ứng tuyển',
  ApplicationExpired: 'Đơn ứng tuyển hết hạn',
  ApplicationClosed: 'Chiến dịch ngừng nhận ứng tuyển',
  BriefClaimed: 'Creator nhận brief',
  DealPublished: 'Đã đăng',
  DealCompleted: 'Hoàn tất, creator đã nhận tiền',
  DealDeclined: 'Creator từ chối lời mời',
  DealExpired: 'Lời mời hết hạn',
  DealCancelled: 'Đơn đã huỷ',
  DealForfeited: 'Bài bị gỡ trước hạn, creator không nhận tiền',
  // Ghi đè các nhãn cũ có chữ "Doanh nghiệp": phòng trao đổi gọi bên đặt đơn là "người đặt".
  DisputeResponded: 'Người đặt giải trình',
  AdminRefunded: 'Admin hoàn tiền cho người đặt',
  DealPublishFailed: 'Đăng bài lỗi, đã hoàn tiền cho người đặt',
  ExistingPostUnavailable: 'Bài có sẵn không còn dùng được, người đặt cần chọn lại',
  ProposalSent: 'Creator gửi đề xuất',
  ProposalAccepted: 'Người đặt chọn đề xuất',
  ProposalDeclined: 'Người đặt chưa chọn đề xuất',
  ProposalWithdrawn: 'Creator rút đề xuất',
  ProposalExpired: 'Đề xuất hết hạn',
};

export const threadEventLabel = (item: Pick<BrandThreadItem, 'eventKind' | 'seq' | 'max' | 'actor' | 'dealKind'>): string => {
  const kind = item.eventKind ?? '';
  const live = item.dealKind === 'Live';
  switch (kind) {
    case 'DealInvited':
      return live ? 'Người đặt gửi đơn quảng cáo livestream' : 'Người đặt gửi lời mời';
    case 'DealAccepted':
      return live ? 'Creator nhận quảng cáo livestream' : 'Creator nhận lời mời';
    case 'Submitted':
      return item.seq ? `Gửi bản ${item.seq}` : 'Gửi bài';
    case 'RevisionRequested':
      if (!item.seq) return 'Yêu cầu sửa';
      return item.max ? `Yêu cầu sửa lần ${item.seq}/${item.max}` : `Yêu cầu sửa lần ${item.seq}`;
    case 'DirectEditRequested':
      if (!item.seq) return 'Đề nghị chỉnh';
      return item.max ? `Đề nghị chỉnh lần ${item.seq}/${item.max}` : `Đề nghị chỉnh lần ${item.seq}`;
    case 'DealContentUpdated':
      // Bản 1 là nội dung lúc mời nên lần lưu thứ n là bản n + 1.
      return item.seq ? `Người đặt cập nhật nội dung (bản ${item.seq + 1})` : 'Người đặt cập nhật nội dung';
    case 'BriefUpdated':
      // Bản 1 là brief của chiến dịch lúc mời nên lần lưu thứ n là bản n + 1.
      return item.seq ? `Người đặt cập nhật brief (bản ${item.seq + 1})` : 'Người đặt cập nhật brief';
    case 'Approved':
      return 'Đã duyệt';
    case 'AutoApproved':
      return 'Hệ thống tự duyệt do quá hạn';
    case 'Rejected':
      return 'Người đặt từ chối bài';
    case 'CancelRequested':
      return item.actor === 'Creator' ? 'Creator xin huỷ' : 'Người đặt xin huỷ';
    default:
      return SYNTHETIC_LABELS[kind] ?? taskEventLabel(kind);
  }
};

export const revisionCounterLabel = (r: BrandThreadRevision): string => `Đã yêu cầu sửa ${r.count}/${r.max}`;

export const threadDealStatusLabel = (status: string): string => {
  if (status === 'Airing') return 'Đang chạy quảng cáo';
  if (status === 'PendingApproval') return 'Chờ admin duyệt';
  return taskStatusLabel(status as BrandTaskStatus);
};

const namedPart = (label: string, name: string | null | undefined) => (name && name.trim() ? `${label}: ${name.trim()}` : label);

/** Dòng "đang trao đổi với ai" ở đầu phòng. Thiếu tên thì chỉ ghi vai, không để "Người đặt: " trống. */
export const threadCounterpartLabel = (t: Pick<BrandThreadTimeline, 'myRole' | 'creatorName' | 'ordererName'>): string => {
  const orderer = namedPart('Người đặt', t.ordererName);
  const creator = namedPart('Creator', t.creatorName);
  if (t.myRole === 'Creator') return orderer;
  if (t.myRole === 'Orderer') return creator;
  return `${orderer} · ${creator}`;
};
